import { useCartStore } from '@/stores/cart.js'
import { useCustomerStore } from '@/stores/customer.js'
import { useDeviceStore } from '@/stores/device.js'
import { useUiStore } from '@/stores/ui.js'
import { inject, computed } from 'vue'
import { useNotification } from '@/composables/useNotification.js'
import { getErrorMessage } from '@/utils/error.js'
import { useCart } from '@/composables/useCart.js'
import { useI18n } from 'vue-i18n'
export function useShipment() {
  const cartStore = useCartStore()
  const customerStore = useCustomerStore()
  const deviceStore = useDeviceStore()
  const uiStore = useUiStore()
  const { cartUpdate, addLineItem, deleteLineItem } = useCart()
  const { showNotification } = useNotification()
  const { t } = useI18n()
  const cl = inject('cl')

  async function setShippingMethod(shippingMethodReference, cartId) {
    await cl.orders.update({
      id: cartId,
      metadata: {
        delivery_method: shippingMethodReference
      }
    })
    await setClShippingMethod(shippingMethodReference, cartId)
    cartStore.hasValidShippingMethod = true
  }

  async function setClShippingMethod(shippingMethodReference, cartId, onOrderApprove = false) {
    const order = await cl.orders.retrieve(cartId, {
      include: [
        'shipments.shipping_method.shipping_zone',
        'shipments.available_shipping_methods.shipping_zone',
        'line_items',
        'market'
      ]
    })

    const shipment = order?.shipments[0] || null
    if (!shipment) {
      cartStore.hasValidShippingMethod = false
      return
    }

    const shippingMethod = shipment.available_shipping_methods?.find(
      (shippingMethod) => shippingMethod.reference === shippingMethodReference
    )

    if (!shippingMethod) {
      cartStore.hasValidShippingMethod = false
      return
    }

    cartStore.hasValidShippingMethod = true

    // if order already has same shipping method, we can skip the update
    if (shipment.shipping_method?.id === shippingMethod.id) {
      cartStore.hasValidShippingMethod = true
      return
    }

    const data = {
      id: shipment.id,
      shipping_method: cl.shipping_methods.relationship(shippingMethod.id)
    }

    const shippingCostLineItem = order.line_items.find(
      (lineItem) => lineItem.metadata?.type === 'shipping_cost'
    )

    const shippingCostSkuCode = shippingMethod.metadata?.linked_shipping_cost_sku_code
    const isSameCountryShipping =
      order.market.metadata?.country === order.shipping_address?.country_code
    let shippingCostAdded = false
    let shippingCostUpdated = false

    if (isSameCountryShipping) {
      // If shipping is within the same country, remove any existing shipping cost line item
      if (shippingCostLineItem) {
        await deleteLineItem(shippingCostLineItem.id)
        shippingCostUpdated = true
      }
    } else if (shippingCostSkuCode) {
      // If shipping is not within the same country and a shipping cost SKU code is provided
      if (!shippingCostLineItem) {
        // Add a new shipping cost line item if it doesn't exist
        await addLineItem({
          sku_code: shippingCostSkuCode,
          quantity: 1,
          metadata: {
            type: 'shipping_cost',
            zone_name: shippingMethod.shipping_zone.name.toUpperCase(),
            shipping_method: shippingMethod.name,
            shipping_method_id: shippingMethod.id
          }
        })
        shippingCostAdded = true
        shippingCostUpdated = true
      }
    } else if (shippingCostLineItem) {
      // If no SKU code is provided but a shipping cost line item exists, remove it
      await deleteLineItem(shippingCostLineItem.id)
      shippingCostUpdated = true
    }

    if (shippingCostUpdated) {
      // if we updated the shipping cost line item, we need to recalculate the order
      // we need to refresh the shipments after adding the line item - as the shipment was deleted during the addLineItem call
      const updatedOrder = await cl.orders.retrieve(cartId, {
        include: ['shipments']
      })
      const updatedShipment = updatedOrder?.shipments[0] || null
      data.id = updatedShipment.id
    }

    // update shipping method after adding line item - as adding line item refreshes the order and removes the shipping method
    await cl.shipments.update(data, {
      include: ['shipping_method']
    })

    if (shippingCostAdded && onOrderApprove) {
      // if we added a shipping cost line item, we need to recalculate the payment total
      throw new Error(t('cart.shippingCostRecalculationWarning'))
    }
  }

  async function setBillingAddress(billingAddressId) {
    try {
      await cartUpdate({
        id: customerStore.customer.currentCart.id,
        _billing_address_clone_id: billingAddressId
      })
      cartStore
    } catch (error) {
      showNotification({
        message: getErrorMessage(error),
        variant: 'danger'
      })
    }
  }

  async function setShippingAddressAndMethod(shippingMethodReference, shippingAddressId) {
    const storeAddressId = computed(() => deviceStore.getStoreAddressId)

    if (!shippingAddressId) {
      showNotification({
        message: 'setShippingAddressAndMethod: shippingAddressId is required',
        variant: 'danger'
      })
    }

    try {
      const cartMeta = customerStore.customer.currentCart.metadata

      if (shippingMethodReference === 'SHIPPING') {
        delete cartMeta.pickup_location
      }

      const data = {
        id: customerStore.customer.currentCart.id,
        ...(storeAddressId.value !== shippingAddressId && {
          _shipping_address_clone_id: shippingAddressId
        }),
        ...(storeAddressId.value === shippingAddressId && {
          shipping_address: cl.addresses.relationship(shippingAddressId)
        }),
        metadata: {
          ...cartMeta,
          ...(shippingMethodReference === 'PICKUP' && {
            pickup_location: cartStore.pickupLocation
          }),
          delivery_method: shippingMethodReference
        }
      }

      await cartUpdate(data)
      await setClShippingMethod(shippingMethodReference, customerStore.customer.currentCart.id)
    } catch (error) {
      showNotification({
        message: getErrorMessage(error),
        variant: 'danger'
      })
    }
  }

  async function fetchAvailableAllShippingMethods(filters = {}) {
    return await cl.shipping_methods.list({
      filters: {
        disabled_at_null: true,
        ...filters
      },
      include: ['shipping_zone'],
      fields: {
        shipping_methods: ['reference', 'shipping_zone', 'currency_code'],
        shipping_zones: ['name', 'country_code_regex', 'not_country_code_regex']
      }
    })
  }

  /**
   * Get available shipping methods for the current order country code.
   * All available shipping methods are loaded on store initialization.
   *
   * @param {*} shippingMethod
   * @param {*} countryCode
   * @returns
   */
  function getAvailableShippingMethods(shippingMethod = null, countryCode = null) {
    const currentOrderCountryCode = uiStore.currentOrderCountryCode
      ? uiStore.currentOrderCountryCode.toUpperCase()
      : deviceStore.settings.country.toUpperCase()
    countryCode = countryCode ? countryCode : currentOrderCountryCode
    // filter available shipping methods form device store by customerStore.customer.currentCart.currency_code
    const availableShippingMethods =
      deviceStore.settings.availableShippingMethods?.filter((method) =>
        getAvailableZoneCountries(method).includes(countryCode)
      ) || []

    if (shippingMethod) {
      return availableShippingMethods.filter((method) => method.reference === shippingMethod)
    }

    return availableShippingMethods
  }

  /**
   * Get the shipping method by reference and zone.
   * You can also specify the country code of the order and the available shipping methods.
   * If not specified, the default values are used.
   *
   * Shipping cost zone is used to filter the shipping methods by zone name.
   *
   * @param {*} countryCode
   * @param {*} availableShippingMethods
   * @param {*} shippingMethodReference
   * @param {*} shippingCostZone
   * @returns object
   */
  function getShippingMethodByReferenceAndZone(
    countryCode,
    availableShippingMethods = [],
    shippingMethodReference = 'SHIPPING',
    shippingCostZone = null
  ) {
    if (availableShippingMethods.length === 0) {
      availableShippingMethods = deviceStore.settings.availableShippingMethods
    }

    if (shippingCostZone) {
      return (
        availableShippingMethods.find(
          (method) =>
            countryIsInShippingZone(countryCode, method) &&
            method.currency_code === 'EUR' &&
            method.reference === shippingMethodReference &&
            method.shipping_zone.name.toUpperCase() === shippingCostZone
        ) || []
      )
    } else if (['AT', 'DE'].includes(countryCode.toUpperCase())) {
      return (
        availableShippingMethods.find(
          (method) =>
            countryIsInShippingZone(countryCode, method) &&
            method.currency_code === 'EUR' &&
            method.reference === shippingMethodReference &&
            method.shipping_zone.name.toUpperCase() !== 'EUROPE'
        ) || []
      )
    } else {
      return (
        availableShippingMethods.find(
          (method) =>
            countryIsInShippingZone(countryCode, method) &&
            method.reference === shippingMethodReference
        ) || []
      )
    }
  }

  /**
   * Get the available countries for a shipping method.
   *
   * if no international shipping is enabled, DE will only ship to DE, AT will only ship to AT
   *
   * all other countries will ship to all countries in the shipping zone
   *
   * @param {*} method
   * @param {*} countryCode
   * @returns array
   */
  function getShippingZonesCountries(method, countryCode) {
    let countries = []
    if (!method?.shipping_zone) {
      return countries
    }

    countries = method.shipping_zone.country_code_regex.split('|')

    if (['EUROPE'].includes(method.shipping_zone.name.toUpperCase())) {
      // specific case for EUROPE zone - we always add DE to the list of countries
      countries.push('DE')
    } else if (['AT', 'DE'].includes(countryCode.toUpperCase())) {
      countries = [countryCode.toUpperCase()]
    }

    return countries
  }

  function countryIsInShippingZone(countryCode, shippingMethod) {
    const availableCountries = getShippingZonesCountries(shippingMethod, countryCode)
    return availableCountries.includes(countryCode.toUpperCase())
  }

  /**
   * Check if the given country code is available for shipping.
   * You can also specify the country code of the order, whether to check for the same country only,
   * and the available shipping methods. If not specified, the default values are used.
   * @param {*} countryCodeAddress
   * @param {*} countryCodeOrder
   * @param {*} sameCountryOnly
   * @param {*} availableShippingMethods
   * @returns boolean
   */
  function countryIsAvailableForShipping(
    countryCodeAddress,
    countryCodeOrder,
    availableShippingMethods = [],
    shippingCostZone = null
  ) {
    const method = getShippingMethodByReferenceAndZone(
      countryCodeOrder,
      availableShippingMethods,
      'SHIPPING',
      shippingCostZone
    )
    const availableCountries = getShippingZonesCountries(method, countryCodeOrder)

    return availableCountries.includes(countryCodeAddress.toUpperCase())
  }

  /**
   * Get the available countries in the shipping zone.
   * You can also specify the shipping method reference, the country code of the order and the available shipping methods. If not specified, the default values are used.
   *
   * @param {*} shippingMethodReference
   * @param {*} countryCodeOrder
   * @param {*} availableShippingMethods
   * @returns array
   */
  function getAvailableZoneCountries(
    countryCodeOrder,
    availableShippingMethods = [],
    shippingMethodReference = 'SHIPPING',
    shippingCostZone = null
  ) {
    if (!shippingCostZone && ['DE', 'AT'].includes(countryCodeOrder?.toUpperCase())) {
      // if country code is DE or AT, we only ship to the same country
      return [countryCodeOrder.toUpperCase()]
    }
    const method = getShippingMethodByReferenceAndZone(
      countryCodeOrder,
      availableShippingMethods,
      shippingMethodReference,
      shippingCostZone
    )

    if (!method?.shipping_zone) {
      return []
    }

    return getShippingZonesCountries(method, countryCodeOrder)
  }

  return {
    setShippingMethod,
    setClShippingMethod,
    //setShippingAddress,
    setBillingAddress,
    setShippingAddressAndMethod,
    getAvailableShippingMethods,
    getShippingMethodByReferenceAndZone,
    getAvailableZoneCountries,
    fetchAvailableAllShippingMethods,
    countryIsAvailableForShipping
  }
}
